// #664 item 1: wherever a tier is chosen, show the model it resolves to for this tenant's connected
// provider(s), read from `GET /v1/llm/tiers`. Three views ask the same question (the cap fields, task
// create, task edit), so they share one loader rather than three copies of the fetch-and-fall-back dance.

import { computed, ref } from 'vue'
import { api, type LlmTierEntry } from '@/api'

export function useLlmTiers() {
    const entries = ref<LlmTierEntry[]>([])
    const loading = ref(false)
    /** True once a fetch has finished, whether it answered or failed: the loading state ends either way. */
    const settled = ref(false)

    async function loadLlmTiers(): Promise<void> {
        loading.value = true
        try {
            entries.value = await api.llmTiers()
        } catch {
            // A tenant with no connection, or an endpoint that is down, must not break the tier picker:
            // it falls back to the bare tier label it showed before this existed.
            entries.value = []
        } finally {
            loading.value = false
            settled.value = true
        }
    }

    /** What one tier resolves to, preferring the provider a live call would use first. */
    function modelForTier(tier: string | null | undefined): string {
        return entries.value.find(entry => entry.tier === tier)?.model ?? ''
    }

    /** What to show beside the tier picker: the model, "…" while loading, "—" when we cannot say. */
    function tierModelLabel(tier: string | null | undefined): string {
        const model = modelForTier(tier)
        if (model) {
            return model
        }
        return settled.value ? '—' : '…'
    }

    /** Every distinct model this tenant's connected providers offer, for the explicit-model picker. */
    const models = computed(() => [...new Set(entries.value.map(entry => entry.model).filter(Boolean))].sort())

    return { entries, loading, settled, loadLlmTiers, modelForTier, tierModelLabel, models }
}
